import { DateObject } from "../models/DateObject";
import { FuturePayment } from "../models/FuturePayment";
import { Injectable } from "@angular/core";
import { AppDate } from "@app/util/app-date";

/**
 * Created by mac85103 on 7/21/16.
 */

export type IUrlEnv = { env: "dv" | "qa" | "ut" | "pd", url: string }

@Injectable()
export class UtilService {
    constructor() { }

    /**
     * Returns {Number}
     * @param v1 {string} The first version to be compared.
     * @param v2 {string} The second version to be compared.
     * @returns {Number} <ul>
     *    <li>0 if the versions are equal</li>
     *    <li>a negative integer iff v1 < v2</li>
     *    <li>a positive integer iff v1 > v2</li>
     *    <li>NaN if either version string is in the wrong format</li>
     * </ul>
     * @memberof Util
     *
     */
    versionCompare(v1: string, v2: string): number {
        if (v1 == null) {
            v1 = "0.0.0";
        }
        if (v2 == null) {
            v2 = "0.0.0";
        }

        var v1parts = v1.split('.');
        var v2parts = v2.split('.');

        for (var i = 0; i < v1parts.length; ++i) {
            if (v2parts.length === i) {
                return 1;
            }
            if (v1parts[i] === v2parts[i]) {
                continue;
            } else if (v1parts[i] > v2parts[i]) {
                return 1;
            } else {
                return -1;
            }
        }
        if (v1parts.length !== v2parts.length) {
            return -1;
        }

        return 0;
    }

    dateFormat(month: number, day: number, year: number): string {
        return month + "/" + day + "/" + year.toString().substr(2, 2);
    }

    toTitleCase(str: string): string {
        return str.replace(/\w\S*/g, (txt: string) => {
            return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase();
        });
    }

    extendObj<A, B>(objA: A, objB: B): A & B {
        const res = objA as A & B
        for (let key in objB) {
            // @ts-ignore
            res[key] = objB[key] as (A & B)[keyof B]
        }
        return res
    }

    formatDateObject(date: Date): DateObject {
        return {
            day: date.getDate(),
            month: date.getMonth() + 1,
            year: date.getFullYear(),
            formattedFullDate: ('0' + (date.getMonth() + 1)).slice(-2) + '/'
                + ('0' + date.getDate()).slice(-2) + '/'
                + date.getFullYear()
        }
    }

    findNextPayment(futurePayments: FuturePayment[]): FuturePayment {
        let nextFuturePaymentDate = AppDate.dateFromDateObject(futurePayments[0].date);
        let nextFuturePayment = futurePayments[0];
        for (let i = 1; i < futurePayments.length; i++) {
            let tempDate = AppDate.dateFromDateObject(futurePayments[i].date);
            if (nextFuturePaymentDate > tempDate) {
                nextFuturePaymentDate = tempDate;
                nextFuturePayment = futurePayments[i];
            }
        }
        return nextFuturePayment;
    }

    // https://support.okta.com/help/s/article/Password-rejected-even-though-it-meets-all-requirements
    // Check to see if "Does not contain part of username" option is selected in password policy.  The logic this setting uses is as follows:
    // The policy analyzes the username as "parts" that are separated by punctuation
    // Any part that contains less than 4 characters is not considered by the policy
    // The password cannot contain any of these individual parts in their entirety, but can contain a set of characters that comprise a portion of an individual part
    // https://developer.okta.com/docs/reference/api/users/#default-password-policy
    // Must not contain the user's login or parts of the the login when split on the following characters: , . _ # @
    // For example, a user with login isaac.brock@example.com will not be able set password brockR0cks! as the password contains the login part brock
    hasPartOfEmail(password: string, email: string): boolean {
        if (password === undefined || email === undefined || email === null || password === null)
            return false;
        const parts = email.split(/[.@-_+]/).filter(p => p.length >= 4);
        const matches = parts.filter(p => new RegExp(p, 'gi').test(password));
        return matches.length > 0;
    }

    // Does not contain first name or Does not contain last name option excludes all of the user's first name or last name in its entirety.
    // Checking this option ensures that a password can't contain all of the first or last name.
    // This option only applies to names that are at least three characters long and isn't case sensitive.
    hasName(password: string, name: string): boolean {
        if (!password || !name || name.length < 3)
            return false;
        return new RegExp(name, 'gi').test(password);
    }
}
